import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Input, Select, cn } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import LanguagePreferenceToggle from '../../components/Leads/LanguagePreferenceToggle';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import {
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import {
  LEAD_SOURCE_FORM_OPTIONS,
  LEAD_TYPE_LABELS,
  leadTypesForChannel,
} from '../../lib/leadMeta';

const { FiX, FiAlertTriangle, FiArchive, FiTrash2 } = FiIcons;

const CO_WORKER_ADMIN_ROLES = new Set(['admin', 'super_admin']);

const LEAD_SOURCE_OPTIONS = LEAD_SOURCE_FORM_OPTIONS;

const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Follow Up', value: 'follow_up' },
  { label: 'Appointment Set', value: 'appointment_set' },
  { label: 'Closed Won', value: 'closed_won' },
  { label: 'Closed Lost', value: 'closed_lost' },
  { label: 'Do Not Contact', value: 'dnc' },
];

const GENDER_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Non-binary', value: 'Non-binary' },
  { label: 'Prefer not to say', value: 'Prefer not to say' },
];

const MARITAL_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Single', value: 'Single' },
  { label: 'Married', value: 'Married' },
  { label: 'Divorced', value: 'Divorced' },
  { label: 'Widowed', value: 'Widowed' },
];

const TIMEZONE_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Eastern', value: 'Eastern' },
  { label: 'Central', value: 'Central' },
  { label: 'Mountain', value: 'Mountain' },
  { label: 'Pacific', value: 'Pacific' },
  { label: 'Alaska', value: 'Alaska' },
  { label: 'Hawaii', value: 'Hawaii' },
];

const POLICY_TYPE_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Term', value: 'Term' },
  { label: 'Whole Life', value: 'Whole Life' },
  { label: 'Universal Life', value: 'Universal Life' },
  { label: 'Final Expense', value: 'Final Expense' },
  { label: 'Annuity', value: 'Annuity' },
  { label: 'Other', value: 'Other' },
];

const TERM_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: '10 Year', value: '10 Year' },
  { label: '15 Year', value: '15 Year' },
  { label: '20 Year', value: '20 Year' },
  { label: '25 Year', value: '25 Year' },
  { label: '30 Year', value: '30 Year' },
  { label: 'N/A', value: 'N/A' },
];

function coWorkerDisplayName(lead) {
  return resolveDisplayName(lead?.co_worker_id, lead?.co_worker) || '—';
}

function ReadOnlyUserField({ label, name }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <p className="text-sm font-medium text-gray-900 py-2">{name || '—'}</p>
    </div>
  );
}

function todayDateKey() {
  return new Date().toISOString().slice(0, 10);
}

function buildFormState(lead) {
  return {
    channel: lead?.channel || null,
    created_by: lead?.created_by ?? null,
    first_name: lead?.first_name || '',
    last_name: lead?.last_name || '',
    phone: lead?.phone || '',
    phone_secondary: lead?.phone_secondary || '',
    email: lead?.email || '',
    lead_type: lead?.lead_type || 'fresh',
    lead_source: lead?.lead_source || 'manual',
    lead_vendor: lead?.lead_vendor || '',
    status: lead?.status || 'new',
    co_worker_id: lead?.co_worker_id ?? null,
    dob: lead?.dob || '',
    gender: lead?.gender || '',
    marital_status: lead?.marital_status || '',
    smoker: lead?.smoker ?? false,
    height_feet: lead?.height_feet ?? '',
    height_inches: lead?.height_inches ?? '',
    weight: lead?.weight ?? '',
    address: lead?.address || '',
    city: lead?.city || '',
    state: lead?.state || '',
    zip: lead?.zip || '',
    time_zone: lead?.time_zone || '',
    coverage_amount_requested: lead?.coverage_amount_requested || '',
    policy_type_requested: lead?.policy_type_requested || '',
    term_requested: lead?.term_requested || '',
    notes: lead?.notes || '',
    language: lead?.language || 'en',
  };
}

function getChangedFields(original, current) {
  const changed = {};
  for (const key of Object.keys(current)) {
    const origVal = original[key];
    const curVal = current[key];
    if (origVal !== curVal) {
      changed[key] = curVal === '' ? null : curVal;
    }
  }
  return changed;
}

function SectionHeading({ children }) {
  return (
    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3 mt-1">{children}</h3>
  );
}

function FieldGrid({ children, className }) {
  return (
    <div className={cn('grid grid-cols-2 gap-3', className)}>
      {children}
    </div>
  );
}

function FieldFull({ children }) {
  return <div className="col-span-2">{children}</div>;
}

export default function EditLeadDrawer({
  isOpen,
  onClose,
  lead,
  onSave,
  canArchive = false,
  canDelete = false,
  onArchive,
  onDelete,
}) {
  const { user } = useAuth();
  const { users: directoryUsers } = useUsersDirectory();
  const [form, setForm] = useState(() => buildFormState(lead));
  const [saving, setSaving] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const originalRef = useRef(buildFormState(lead));
  const panelRef = useRef(null);

  const canEditOwner = Boolean(user && CO_WORKER_ADMIN_ROLES.has(user.role));

  const canEditCoWorker = Boolean(
    user &&
    lead &&
    (lead.created_by === user.id || CO_WORKER_ADMIN_ROLES.has(user.role)),
  );

  const showCoWorkerField = canEditCoWorker || Boolean(lead?.co_worker_id || lead?.co_worker);

  const ownerName = useMemo(
    () => resolveDisplayName(lead?.created_by, lead?.owner),
    [lead?.owner, lead?.created_by, directoryUsers],
  );

  const assignedName = useMemo(
    () => resolveDisplayName(lead?.exclusive_to, lead?.exclusive_holder),
    [lead?.exclusive_holder, lead?.exclusive_to, directoryUsers],
  );

  useEffect(() => {
    if (isOpen && lead) {
      const fresh = buildFormState(lead);
      setForm(fresh);
      originalRef.current = fresh;
      setShowUnsavedWarning(false);
    }
  }, [isOpen, lead]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, form]);

  const isDirty = useCallback(() => {
    return Object.keys(form).some((k) => form[k] !== originalRef.current[k]);
  }, [form]);

  const handleClose = useCallback(() => {
    if (isDirty()) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleForceClose = () => {
    setShowUnsavedWarning(false);
    onClose();
  };

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'channel') {
        const allowed = leadTypesForChannel(value);
        if (next.lead_type && !allowed.includes(next.lead_type)) {
          next.lead_type = allowed[0] || 'fresh';
        }
      }
      return next;
    });
  };

  const setChannel = (ch) => {
    setForm((prev) => {
      const allowed = leadTypesForChannel(ch);
      let lead_type = prev.lead_type;
      if (lead_type && !allowed.includes(lead_type)) {
        lead_type = allowed[0] || 'fresh';
      }
      return { ...prev, channel: ch, lead_type };
    });
  };

  const setCoWorkerId = (coWorkerId) => {
    setForm((prev) => ({ ...prev, co_worker_id: coWorkerId }));
  };

  const setOwnerId = (ownerId) => {
    setForm((prev) => ({ ...prev, created_by: ownerId }));
  };

  const leadTypeOptions = (form.channel
    ? leadTypesForChannel(form.channel)
    : []
  ).map((value) => ({ value, label: LEAD_TYPE_LABELS[value] || value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const changed = getChangedFields(originalRef.current, form);
    const originalOwnerId = lead?.created_by ?? null;
    const selectedOwnerId = form.created_by ?? null;
    const ownershipChanged = selectedOwnerId !== originalOwnerId;

    if (ownershipChanged) {
      changed.created_by = selectedOwnerId;
      changed.exclusive_to = selectedOwnerId;
    }

    if (Object.keys(changed).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await onSave(changed);
      if (ownershipChanged) {
        const previousOwnerName =
          resolveDisplayName(originalOwnerId, lead?.owner) || 'Unknown';
        const newOwnerName = resolveDisplayName(selectedOwnerId) || 'Unknown';
        const adminName = resolveDisplayName(user?.id, user) || user?.full_name || 'Unknown';
        await api.activityLogs.create(lead.id, {
          activity_type: 'manual_calls',
          activity_date: todayDateKey(),
          notes: `Lead ownership manually transferred from ${previousOwnerName} to ${newOwnerName} by ${adminName}.`,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-lead-title"
        className="relative z-10 flex flex-col w-[80vw] max-w-[900px] max-h-[90vh] bg-white rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — fixed, does not scroll */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
          <h2 id="edit-lead-title" className="text-lg font-semibold text-gray-900">Edit Lead</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {showUnsavedWarning && (
          <div className="flex-shrink-0 mx-6 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Unsaved changes</p>
              <p className="text-xs text-yellow-700 mt-0.5">You have unsaved changes. Discard them?</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="danger" onClick={handleForceClose}>
                  Discard
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowUnsavedWarning(false)}>
                  Keep Editing
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable form body */}
        <form
          id="edit-lead-form"
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6"
        >
          <section>
            <SectionHeading>Contact Info</SectionHeading>
            <FieldGrid>
              <Input label="First Name *" value={form.first_name} onChange={set('first_name')} required />
              <Input label="Last Name" value={form.last_name} onChange={set('last_name')} />
              <Input label="Phone *" value={form.phone} onChange={set('phone')} required />
              <Input label="Phone Secondary" value={form.phone_secondary} onChange={set('phone_secondary')} />
              <FieldFull>
                <Input label="Email" type="email" value={form.email} onChange={set('email')} />
              </FieldFull>
            </FieldGrid>
          </section>

          <section>
            <SectionHeading>Lead Info</SectionHeading>
            <FieldGrid>
              <FieldFull>
                <label className="block text-sm font-medium text-gray-700 mb-2">Channel</label>
                <ChannelSelector value={form.channel} onChange={setChannel} hideAllChip size="sm" />
              </FieldFull>
              <Select
                label="Lead Type"
                value={form.lead_type}
                onChange={set('lead_type')}
                options={leadTypeOptions}
                disabled={!form.channel}
              />
              <Select label="Lead Source" value={form.lead_source} onChange={set('lead_source')} options={LEAD_SOURCE_OPTIONS} />
              {form.lead_source === 'purchased' && (
                <FieldFull>
                  <Input label="Lead Vendor" value={form.lead_vendor} onChange={set('lead_vendor')} placeholder="Vendor name" />
                </FieldFull>
              )}
              <Select label="Status" value={form.status} onChange={set('status')} options={STATUS_OPTIONS} />
              {canEditOwner ? (
                <UserPickerDropdown
                  label="Owner"
                  source="directory"
                  value={form.created_by}
                  onChange={setOwnerId}
                  disabled={saving}
                />
              ) : (
                <ReadOnlyUserField label="Owner" name={ownerName} />
              )}
              <ReadOnlyUserField label="Assigned To (Exclusivity)" name={assignedName} />
              {showCoWorkerField && (
                <div>
                  {canEditCoWorker ? (
                    <UserPickerDropdown
                      label="Co-Worker"
                      source="directory"
                      value={form.co_worker_id}
                      onChange={setCoWorkerId}
                      placeholder="None"
                      disabled={saving}
                    />
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Co-Worker</label>
                      <p className="text-sm font-medium text-gray-900 py-2">{coWorkerDisplayName(lead)}</p>
                    </>
                  )}
                </div>
              )}
            </FieldGrid>
          </section>

          <section>
            <SectionHeading>Personal Info</SectionHeading>
            <FieldGrid>
              <Input label="Date of Birth" type="date" value={form.dob} onChange={set('dob')} />
              <Select label="Gender" value={form.gender} onChange={set('gender')} options={GENDER_OPTIONS} />
              <Select label="Marital Status" value={form.marital_status} onChange={set('marital_status')} options={MARITAL_OPTIONS} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Smoker</label>
                <label className="relative inline-flex items-center cursor-pointer mt-1.5">
                  <input
                    type="checkbox"
                    checked={form.smoker}
                    onChange={set('smoker')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
                  <span className="ml-3 text-sm text-gray-600">{form.smoker ? 'Yes' : 'No'}</span>
                </label>
              </div>
              <Input label="Height (ft)" type="number" min="0" max="9" value={form.height_feet} onChange={set('height_feet')} />
              <Input label="Height (in)" type="number" min="0" max="11" value={form.height_inches} onChange={set('height_inches')} />
              <FieldFull>
                <Input label="Weight (lbs)" type="number" min="0" value={form.weight} onChange={set('weight')} />
              </FieldFull>
            </FieldGrid>
          </section>

          <section>
            <SectionHeading>Address</SectionHeading>
            <FieldGrid>
              <FieldFull>
                <Input label="Address" value={form.address} onChange={set('address')} />
              </FieldFull>
              <Input label="City" value={form.city} onChange={set('city')} />
              <Input label="State" value={form.state} onChange={set('state')} />
              <Input label="Zip" value={form.zip} onChange={set('zip')} />
              <Select label="Time Zone" value={form.time_zone} onChange={set('time_zone')} options={TIMEZONE_OPTIONS} />
            </FieldGrid>
          </section>

          <section>
            <SectionHeading>Coverage Interest</SectionHeading>
            <FieldGrid>
              <FieldFull>
                <Input label="Coverage Amount Requested" value={form.coverage_amount_requested} onChange={set('coverage_amount_requested')} placeholder="e.g. $250,000" />
              </FieldFull>
              <Select label="Policy Type" value={form.policy_type_requested} onChange={set('policy_type_requested')} options={POLICY_TYPE_OPTIONS} />
              <Select label="Term Requested" value={form.term_requested} onChange={set('term_requested')} options={TERM_OPTIONS} />
            </FieldGrid>
          </section>

          <section>
            <SectionHeading>Notes</SectionHeading>
            <textarea
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[100px] resize-y transition-shadow"
              value={form.notes}
              onChange={set('notes')}
              placeholder="Internal notes about this lead…"
            />
          </section>

          <section>
            <SectionHeading>Preferences</SectionHeading>
            <LanguagePreferenceToggle
              value={form.language}
              onChange={(lang) => setForm((prev) => ({ ...prev, language: lang }))}
              disabled={saving}
            />
          </section>
        </form>

        {/* Footer — fixed, does not scroll */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
            <div className="flex flex-col gap-2 min-w-0">
              {canArchive && (
                <Button
                  type="button"
                  variant="outline"
                  className="justify-center"
                  onClick={onArchive}
                  disabled={saving}
                >
                  <SafeIcon icon={FiArchive} className="mr-2 w-4 h-4 text-amber-500" />
                  Archive Lead
                </Button>
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="danger"
                  className="justify-center"
                  onClick={onDelete}
                  disabled={saving}
                >
                  <SafeIcon icon={FiTrash2} className="mr-2 w-4 h-4" />
                  Delete Lead
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <Button type="button" variant="ghost" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" form="edit-lead-form" variant="primary" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner className="mr-2" />
                    Saving…
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
